import { WorkerClient } from "@host/services/workerClient";
import { Game } from "@game/game";
import { GameEvent } from "@game/gameEvent";
import { Player } from "@game/player/player";
import { IRegionData } from "@game/map/region";

const POLL_INTERVAL_MS = 15_000;

interface ILeaderboardEntry {
    nickname: string | null;
    nick_number: number | null;
    word: string;
    score: number;
    region_id: number | null;
    tile_id: number | null;
}

interface INearbyPlayer {
    playerId:  string;
    regionId:  number;
    tileId:    number;
    moved:     number | null;
}

interface IGameState {
    player: {
        playerId: string;
        hand: string[];
        score: number;
        shovels: number;
        location: { regionId: number; tileId: number } | null;
        moved: number | null;
    } | null;
    region: IRegionData | null;
    players: INearbyPlayer[];
    leaderboard: ILeaderboardEntry[];
}

export class DataSubscriptionManager {
    private _intervalId?: ReturnType<typeof setInterval>;
    private _lastLeaderboardScore = 0;
    private _game!: Game;

    async initialise(game: Game): Promise<void> {
        this._game = game;
        void this.poll();
        this._intervalId = setInterval(() => { void this.poll(); }, POLL_INTERVAL_MS);
    }

    destroy(): void {
        if (this._intervalId !== undefined) {
            clearInterval(this._intervalId);
            this._intervalId = undefined;
        }
    }

    /** Call after any action to get the latest state immediately without waiting for the interval. */
    async poll(): Promise<void> {
        if (document.visibilityState === "hidden") return;
        try {
            const client = WorkerClient.instance;
            const params: Record<string, string> = {};

            if (client.isAnonymous) {
                const loc = client.location;

                if (loc) params["regionId"] = String(loc.regionId);
            }
            if (this._game.userPlayer) {
                const ids = this._game.userPlayer.getNearbyRegionIds();

                if (ids.length > 0) params["nearbyRegionIds"] = ids.join(",");
            }
            const state = await client.get<IGameState>("/game/state", params);

            if (state.player && this._game.userPlayer) {
                await this._game.userPlayer.updateFromPoll(state.player);
            }
            if (state.region && this._game.userPlayer) {
                const region = this._game.userPlayer.nearbyRegions[state.region.regionId];

                if (region) region.updateFromPoll(state.region);
            }
            if (this._game.userPlayer) {
                this._game.userPlayer.updateNearbyPlayers(state.players ?? []);
            }
            this.applyLeaderboardUpdate(state.leaderboard);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);

            console.error("Poll error:", message);
        }
    }

    private applyLeaderboardUpdate(leaderboard: ILeaderboardEntry[]): void {
        const top = leaderboard[0];

        if (!top || top.score <= this._lastLeaderboardScore) return;

        this._lastLeaderboardScore = top.score;
        this._game.events.update(new GameEvent("NewHighScore", {
            nickname: Player.MakeNickname(top.nickname ?? undefined, top.nick_number ?? undefined),
            score:    top.score,
            type:     "daily",
            words:    top.word ? [{ word: top.word, score: top.score, region_id: top.region_id, tile_id: top.tile_id }] : []
        }));
    }
}
